import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { gql } from '@apollo/client';
import { appServiceLines, commonEnums } from '@codametrix/ui-common';
import { getServiceDeskClient } from '../clients/service-desk.client';

const toBackendServiceLine = (service: string): string =>
  appServiceLines.isIPC(service) ? commonEnums.ServiceLines.IPC : service;

export interface CoderRecord {
  coderId: string;
  coderName: string;
  queueCount: number;
}

// CodersByProfile returns workers assigned to a given collection (queue).
// Coders returns all active coders; available = all minus assigned.
// These will be replaced by a dedicated GetCodersForQueue query when CDMX-14676 lands.
const GET_CODERS_BY_PROFILE = gql`
  query CodersByProfile($profileId: String) {
    CodersByProfile(profileId: $profileId) {
      data
      total
    }
  }
`;

const GET_ALL_CODERS = gql`
  query Coders {
    Coders {
      data
      total
    }
  }
`;

const ADD_CODER_TO_QUEUE = gql`
  mutation AddCoderToQueue($queueId: String!, $coderId: String!, $service: String!) {
    AddCoderToQueue(queueId: $queueId, coderId: $coderId, service: $service)
  }
`;

const REMOVE_CODER_FROM_QUEUE = gql`
  mutation RemoveCoderFromQueue($queueId: String!, $coderId: String!, $service: String!) {
    RemoveCoderFromQueue(queueId: $queueId, coderId: $coderId, service: $service)
  }
`;

export interface FetchCodersPayload {
  assignedCoders: CoderRecord[];
  availableCoders: CoderRecord[];
  // True when one of the two queries failed but the other succeeded — the modal
  // still populates the available list and surfaces a non-blocking error.
  fetchError: boolean;
}

const fetchCodersForQueue = createAsyncThunk<
  FetchCodersPayload,
  { queueId: string; service: string }
>('manageCoders/fetchCodersForQueue', async ({ queueId }) => {
  const client = getServiceDeskClient();
  await client.refreshToken();

  // Decoupled with allSettled so a CodersByProfile failure does not discard a
  // successful Coders result (and vice versa).
  const [assignedSettled, allSettled] = await Promise.allSettled([
    client.apolloClient.query({
      query: GET_CODERS_BY_PROFILE,
      variables: { profileId: queueId },
      fetchPolicy: 'network-only'
    }),
    client.apolloClient.query({
      query: GET_ALL_CODERS,
      fetchPolicy: 'network-only'
    })
  ]);

  const mapCoder = (c: any): CoderRecord => {
    const fields = c.data ?? c;
    return {
      coderId: fields.user_id,
      coderName: [fields.first_name, fields.last_name].filter(Boolean).join(' '),
      queueCount: fields.queue_count ?? 0
    };
  };

  const assignedRes = assignedSettled.status === 'fulfilled' ? assignedSettled.value : null;
  const allRes = allSettled.status === 'fulfilled' ? allSettled.value : null;

  // CodersByProfile returns { data: coderList[] } — flat worker records.
  // Coders returns { data: { data: workerList[], total } } — nested search response.
  const assigned: CoderRecord[] = (assignedRes?.data?.CodersByProfile?.data ?? []).map(mapCoder);
  const assignedIds = new Set(assigned.map(c => c.coderId));
  const allCodersList: any[] = allRes?.data?.Coders?.data?.data ?? allRes?.data?.Coders?.data ?? [];
  const available: CoderRecord[] = allCodersList
    .map(mapCoder)
    .filter((c: CoderRecord) => !assignedIds.has(c.coderId));

  const fetchError =
    assignedSettled.status === 'rejected' || allSettled.status === 'rejected';

  return { assignedCoders: assigned, availableCoders: available, fetchError };
});

const addCoderToQueue = createAsyncThunk<
  string,
  { queueId: string; coderId: string; service: string }
>('manageCoders/addCoderToQueue', async ({ queueId, coderId, service }) => {
  const client = getServiceDeskClient();
  await client.refreshToken();
  await client.apolloClient.mutate({
    mutation: ADD_CODER_TO_QUEUE,
    variables: { queueId, coderId, service: toBackendServiceLine(service) }
  });
  return coderId;
});

const removeCoderFromQueue = createAsyncThunk<
  string,
  { queueId: string; coderId: string; service: string }
>('manageCoders/removeCoderFromQueue', async ({ queueId, coderId, service }) => {
  const client = getServiceDeskClient();
  await client.refreshToken();
  await client.apolloClient.mutate({
    mutation: REMOVE_CODER_FROM_QUEUE,
    variables: { queueId, coderId, service: toBackendServiceLine(service) }
  });
  return coderId;
});

const openModal = createAction<{ queueId: string; queueName: string }>(
  'manageCoders/openModal'
);

const closeModal = createAction('manageCoders/closeModal');

const stageAddCoder = createAction<{ coderId: string }>(
  'manageCoders/stageAddCoder'
);

const stageRemoveCoder = createAction<{ coderId: string }>(
  'manageCoders/stageRemoveCoder'
);

const cancelChanges = createAction('manageCoders/cancelChanges');

export {
  fetchCodersForQueue,
  addCoderToQueue,
  removeCoderFromQueue,
  openModal,
  closeModal,
  stageAddCoder,
  stageRemoveCoder,
  cancelChanges
};
